import { Component, OnInit } from '@angular/core';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../shared/formly/ga-formly.service';
import { UntypedFormGroup } from '@angular/forms';
import { FormattingService } from '../../shared/services/formatting.service';
import { TranslateService } from '@ngx-translate/core';
import { ICountry, IValueObject } from '../../../type_definitions/datatypes';
import { GaFormlyValidatorService } from '../../shared/formly/ga-formly-validator.service';
import { Transition } from '@uirouter/angular';
import { TitleService } from '../../shared/services/title.service';
import { AccessDataService, ISignupContext, ISignUpRequest, ISignUpResponse } from '../access-data.service';
import * as _ from 'underscore';
import { CountryDataService } from '../../shared/location/country-data.service';
import { GaValidateService } from '../../shared/formly/ga-validate.service';
import { AuthService } from '../../shared/services/auth/auth.service';
import { TranslationService } from '../../shared/translation/translation.service';

const TYPE_PURCHASE:string = 'PURCHASE';
const TYPE_SELL:string = 'SELL';
const TYPE_WEBSHOP:string = 'WEBSHOP';
const TYPE_MANUFACTURER:string = 'MANUFACTURER';

@Component({
  selector: 'signup',
  template: `
	  <h2 *ngIf="loading">
		  <activity-indicator [useDefaultText]="true" class="no-margin"></activity-indicator>
	  </h2>

	  <div *ngIf="error" class="alert alert-danger"><strong>{{error}}</strong></div>

	  <div *ngIf="!error && !loading && !accountCreated">
		  <h2 class="title" translate>APP.ACCESS.SIGNUP.CREATE_ACCOUNT</h2><br/>

		  <div class="alert alert-danger login-failure" *ngIf="createWithFailure">
			  <i class="fa fa-exclamation-circle"></i>
			  <span translate>{{createWithFailure}}</span>
		  </div>

		<formly [form]="form" [fields]="fields" [model]="model" [noValidate]="true">
			<button class="btn" (click)="createAccount()" [busyButton]="creatingAccount">
				<span translate="APP.ACCESS.SIGNUP.CREATE_ACCOUNT"></span>
			</button>
			<return-to-login class="d-inline"></return-to-login>
		</formly>
	  </div>

	  <div *ngIf="!error && !loading && accountCreated">
		  <h2 class="title" translate>APP.ACCESS.SIGNUP.ACCOUNT_CREATED_TITLE</h2>

		  <p *ngIf="sentToApprover">
			  <span translate="APP.ACCESS.SIGNUP.ACCOUNT_CREATED_REVIEW"></span>.<br>
			  <span [innerHTML]="'APP.ACCESS.SIGNUP.ACCOUNT_CREATED_REVIEW_APPROVAL' | translate: { email: emailAddressTo }"></span>
		  </p>
		  <p *ngIf="!sentToApprover">
			  <span [innerHTML]="'APP.ACCESS.SIGNUP.ACCOUNT_CREATED' | translate: { email: emailAddressTo }"></span>
				<no-email-received></no-email-received>
		  </p>

		  <button class="btn" uiSref="access.login">
			  <span translate="APP.ACCESS.RETURN_TO_LOGIN"></span>
		  </button>
	  </div>
	`
})
export class SignupComponent implements OnInit {
  public fields:IExtraFieldConfigurationObject[];
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public model:{
    email?:string;
    firstName?:string;
    lastName?:string;
    company?:string;
    subCompany?:string;
    country?:ICountry;
    type1?:IValueObject;
    comment?:string;
  } = {};
  public accountCreated:boolean;
  public creatingAccount:boolean;
  public createWithFailure:string;
  public loading:boolean;
  public emailAddressTo:string;
  public error:string;
  public sentToApprover:boolean;

  private countryField:IExtraFieldConfigurationObject;
  private labelClass:string = 'col-sm-4';
  private valueClass:string = 'col-sm-8';
  private context:string;
  private showSubcompany:boolean;
  private subcompanyMandatory:boolean;
  private companyType:string;

  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private translateService:TranslateService,
    private translationService:TranslationService,
    private formlyValidatorService:GaFormlyValidatorService,
    private accessDataService:AccessDataService,
    private countryDataService:CountryDataService,
    private transition:Transition,
    private validateService:GaValidateService,
    private authService:AuthService,
    titleService:TitleService
  ) {
    translateService.stream('APP.ACCESS.SIGNUP.CREATE_ACCOUNT')
      .subscribe((translation:string) => {
        titleService.setTitle(translation);
      });
  }

  public ngOnInit():void {
    this.context = this.transition.params()?.context;
    this.loadContext()
      .then((context:ISignupContext) => {
        if (context) {
          if (context.company) {
            this.companyType = context.company.companyType;
            this.showSubcompany = !context.subCompany && context.company.hasSubCompanies;
            this.subcompanyMandatory = !context.subCompany && context.company.subCompanyMandatory;
            this.model.company = context.subCompany? `${context.company.name}<br /><small>${context.subCompany.name}</small>` : context.company.name;

            if (!_.contains([
              context.company.name,
              context.subCompany && context.subCompany.name
            ], this.transition.params()?.company)) {
              this.error = 'Invalid signup link';
            }
          }

          if (context.email) {
            this.model.email = context.email;
          }
        }

        this.fields = this.createFields();
        this.fillFields();
      });
  }

  private loadContext():Promise<ISignupContext> {
    if (this.context) {
      this.loading = true;
      return this.accessDataService.loadSignupContext(this.context)
        .finally(() => {
          this.loading = false;
        });
    } else {
      return Promise.resolve(undefined);
    }
  }

  public accountType():IValueObject {
    if (!this.model.type1) {
      return undefined;
    }

    if (this.model.type1.id === TYPE_PURCHASE) {
      return { id: 'C' };
    } else if (this.model.type1.id === TYPE_SELL) {
      return { id: 'P' };
    } else if (this.model.type1.id === TYPE_WEBSHOP) {
      return { id: 'P' };
    } else if (this.model.type1.id === TYPE_MANUFACTURER) {
      return { id: 'V' };
    }
  }

  public role():string {
    if (this.model.type1 && this.model.type1.id === TYPE_SELL) {
      return 'BP NAM';
    }

    if (this.model.type1 && this.model.type1.id === TYPE_WEBSHOP) {
      return 'BP LAM';
    }
  }

  private fillFields():void {
    this.formlyService.fillField(this.countryField, this.countryDataService.getAllCountries());
  }

  private createFields():IExtraFieldConfigurationObject[] {
    this.countryField = this.formlyService.aSelect('country', 'GA.GENERAL.COUNTRY')
      .isRequired()
      .isDisabled()
      .isLoading()
      .withLabelClass(this.labelClass)
      .withValueClass(this.valueClass)
      .withFormat(this.formattingService.formatCountry)
      .build();

    let companyField:IExtraFieldConfigurationObject, groupField:IExtraFieldConfigurationObject;
    if (this.showSubcompany) {
      companyField = this.formlyService.anInput('subCompany', 'GA.GENERAL.COMPANY')
        .withRequired(this.subcompanyMandatory)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withInfo('APP.ACCESS.SIGNUP.TIP_USE_LEGAL_NAME')
        .build();
      groupField = this.formlyService.aValue('company', 'GA.GENERAL.GROUP')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build();
    } else if (this.model.company) {
      companyField = this.formlyService.aValue('company', 'GA.GENERAL.COMPANY')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build();
    } else {
      companyField = this.formlyService.anInput('company', 'GA.GENERAL.COMPANY')
        .isRequired()
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withInfo('APP.ACCESS.SIGNUP.TIP_USE_COMPANY_GROUP')
        .build();
    }

    let emailField:IExtraFieldConfigurationObject;
    if (this.model.email) {
      emailField = this.formlyService.aValue('email', 'GA.GENERAL.EMAIL')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build();
    } else {
      let validators;
      if (!this.context) {
        validators = {
          workEmail: this.formlyValidatorService.workEmailValidator
        };
      }
      emailField = this.formlyService.anInput('email', 'GA.GENERAL.EMAIL')
        .isRequired()
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withInputType('email')
        .withInfo('APP.ACCESS.SIGNUP.TIP_USE_WORK_EMAIL')
        .withExtraOptions({
          validators
        })
        .build();
    }

    let accountTypeField:IExtraFieldConfigurationObject;
    if (!this.context) {
      accountTypeField = this.formlyService.aSelect('type1', 'APP.ACCESS.SIGNUP.ACCOUNT_TYPE')
        .isRequired()
        .withValueClass(this.valueClass)
        .withLabelClass(this.labelClass)
        .withFormat(this.formattingService.formatLov)
        .withOptions([
          {
            id: TYPE_PURCHASE,
            value: 'APP.ACCESS.SIGNUP.ACCOUNT_TYPE_OPTIONS.PURCHASE'
          },
          {
            id: TYPE_SELL,
            value: 'APP.ACCESS.SIGNUP.ACCOUNT_TYPE_OPTIONS.SELL'
          },
          {
            id: TYPE_WEBSHOP,
            value: 'APP.ACCESS.SIGNUP.ACCOUNT_TYPE_OPTIONS.WEBSHOP'
          },
          {
            id: TYPE_MANUFACTURER,
            value: 'APP.ACCESS.SIGNUP.ACCOUNT_TYPE_OPTIONS.MANUFACTURER'
          }
        ], true)
        .build();

    } else {
      accountTypeField = this.formlyService.aSelect('type1', 'APP.ACCESS.SIGNUP.ACCOUNT_TYPE')
        .isRequired()
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withFormat(this.formattingService.formatLov)
        .withHideExpression(() => this.companyType !== 'P') // hide when company type is not partner
        .withOptions([
          {
            id: TYPE_SELL,
            value: 'APP.ACCESS.SIGNUP.ACCOUNT_TYPE_OPTIONS.SELL'
          },
          {
            id: TYPE_WEBSHOP,
            value: 'APP.ACCESS.SIGNUP.ACCOUNT_TYPE_OPTIONS.WEBSHOP'
          }
        ], true)
        .build();
    }

    return [
      emailField,

      this.formlyService.anInput('firstName', 'GA.GENERAL.FIRSTNAME')
        .isRequired()
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.formlyService.anInput('lastName', 'GA.GENERAL.LASTNAME')
        .isRequired()
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      companyField,
      groupField,

      this.countryField,

      accountTypeField,

      this.formlyService.aTextarea('comment', 'GA.GENERAL.COMMENTS')
        .withPlaceholder(this.translateService.instant('APP.ACCESS.SIGNUP.TIP_REASON_NEW_ACCOUNT'))
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build()
    ].filter((v) => v); //remove undefined fields
  }

  public createAccount():void {
    const newUserData:ISignUpRequest = {
      email: this.model.email,
      firstname: this.model.firstName,
      lastname: this.model.lastName,
      company: this.model.company,
      subCompany: this.model.subCompany,
      country: this.model.country && this.model.country.id,
      accountType: this.accountType(),
      role: this.role(),
      comment: this.model.comment
    };

    // show form errors when the login form is invalid
    this.validateService.validateFields(this.fields, this.model, true)
      .then(() => {
        this.creatingAccount = true;
        return this.accessDataService.signUp(newUserData, this.context);
      })
      .then((response:ISignUpResponse) => {
        this.accountCreated = true;
        this.emailAddressTo = this.model.email;
        this.sentToApprover = response.sentToApprover;
        this.authService.setLogin(newUserData.email);
      })
      .finally(() => {
        this.creatingAccount = false;
        this.createWithFailure = 'APP.ACCESS.SIGNUP.ACCOUNT_CREATE_FAILED';
      });
  }
}